import React, { useState, useCallback, useEffect } from "react";
import { ChevronLeft, ChevronRight, BookOpen } from "lucide-react";
import { questions as allQuestions } from "../data/questions";
import { saveSession } from "../data/practiceStore";
import { makeProgressKey, loadProgress, saveProgress, clearProgress } from "../data/progressStore";
import { recordAttempt } from "../data/statsRepository";
import AnswerSheetCard from "./AnswerSheetCard";

const OPTION_LABELS = ["A", "B", "C", "D"];
const TYPE_LABELS = { listening: "听解", listeningReading: "听读解", reading: "读解" };

const STAR_COUNT = 5;

// J1+ → 4星, J1 → 3星, J2 → 2星, J3 → 1星
const DIFFICULTY_STARS = { 'J1+': 4, 'J1': 3, 'J2': 2, 'J3': 1 };

function DifficultyStars({ difficulty }) {
  const lit = DIFFICULTY_STARS[difficulty] ?? 2;
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: STAR_COUNT }).map((_, i) => (
        <span key={i} className={`text-sm ${i < lit ? "text-yellow-400" : "text-gray-200"}`}>
          ★
        </span>
      ))}
    </div>
  );
}

export default function PracticeView({ chapterId, questionSet }) {
  const chapterData = allQuestions[chapterId] || {};
  const [activeType, setActiveType] = useState("listening");
  const [currentIndex, setCurrentIndex] = useState(0);
  // answers: { [questionId]: selectedOptionIndex }

  useEffect(() => {
    if (questionSet == null) {
      const availableTypes = Object.keys(chapterData).filter(
        (type) => Array.isArray(chapterData[type]) && chapterData[type].length > 0
      );
      if (availableTypes.length > 0 && !availableTypes.includes(activeType)) {
        setActiveType(availableTypes[0]);
      }
    }
  }, [chapterData, activeType, questionSet]);
  const [answers, setAnswers] = useState({});
  const [isFlashcard, setIsFlashcard] = useState(false);

  // If a fixed questionSet is passed (filtered subset), use it directly
  const list = questionSet != null ? questionSet : (chapterData[activeType] || []);
  const progressKey = makeProgressKey(list);

  // 当题目列表切换时（含初次挂载），从 localStorage 加载进度
  // 不使用保存用的 useEffect，避免初次挂载时用空值覆盖已保存数据
  useEffect(() => {
    const { answers: savedAnswers, currentIndex: savedIndex } = loadProgress(progressKey);
    setAnswers(savedAnswers);
    setCurrentIndex(savedIndex);
  }, [progressKey]);
  const question = list[currentIndex] || null;
  const userAnswer = question ? answers[question.id] : undefined;
  // In flashcard mode, treat every question as "answered" (show answer immediately)
  const answered = userAnswer !== undefined || isFlashcard;
  const isCorrect = answered && userAnswer === question?.answer;

  // Compute current session stats from answers + list
  const sessionStats = useCallback(() => {
    let correct = 0;
    let errors = 0;
    list.forEach((q) => {
      const ua = answers[q.id];
      if (ua === undefined) return;
      if (ua === q.answer) correct++;
      else errors++;
    });
    const total = correct + errors;
    return {
      correct,
      totalErrors: errors,
      rate: total > 0 ? ((correct / total) * 100).toFixed(2) : "0.00",
    };
  }, [answers, list]);

  function handleSelect(optionIndex) {
    if (!question || answered) return;
    const newAnswers = { ...answers, [question.id]: optionIndex };
    setAnswers(newAnswers);
    saveSession(list, newAnswers);
    saveProgress(progressKey, newAnswers, currentIndex);
    recordAttempt(null, question.id, optionIndex, optionIndex === question.answer);
  }

  function handleNext() {
    if (currentIndex < list.length - 1) {
      const next = currentIndex + 1;
      setCurrentIndex(next);
      saveProgress(progressKey, answers, next);
    }
  }

  function handlePrev() {
    if (currentIndex > 0) {
      const prev = currentIndex - 1;
      setCurrentIndex(prev);
      saveProgress(progressKey, answers, prev);
    }
  }

  function handleJump(idx) {
    setCurrentIndex(idx);
    saveProgress(progressKey, answers, idx);
  }

  function handleReset() {
    clearProgress(progressKey);
    setAnswers({});
    setCurrentIndex(0);
  }

  function handleTypeChange(type) {
    setActiveType(type);
    setCurrentIndex(0);
  }

  const stats = sessionStats();

  return (
    <div className="flex flex-col lg:flex-row gap-4 flex-1 min-w-0">
      {/* Center: question + feedback */}
      <div className="flex-1 min-w-0">
        {/* Question card */}
        <div className="border-2 border-green-400 rounded-2xl overflow-hidden mb-3">
          {/* Nav bar */}
          <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-green-200">
            <button
              onClick={handlePrev}
              disabled={currentIndex === 0}
              className="flex items-center gap-1 text-sm text-gray-500 px-3 py-1 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-30"
            >
              <ChevronLeft size={14} /> 上一题
            </button>
            <span className="text-xs text-gray-400">
              {list.length > 0 ? `${currentIndex + 1} / ${list.length}` : "—"}
            </span>
            <button
              onClick={handleNext}
              disabled={currentIndex >= list.length - 1}
              className="flex items-center gap-1 text-sm text-gray-500 px-3 py-1 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-30"
            >
              下一题 <ChevronRight size={14} />
            </button>
          </div>

          {/* Body */}
          <div className="bg-gradient-to-b from-green-50 to-white px-8 py-5 min-h-44">
            {question ? (
              <div>
                <p className="font-bold text-gray-800 mb-3">{question.number}</p>
                {question.lines.map((line, i) => (
                  <p key={i} className="text-sm text-gray-700 leading-relaxed mb-1">
                    {line}
                  </p>
                ))}
                {/* Options */}
                <div className="mt-4 space-y-2">
                  {question.options.map((opt, i) => {
                    const label = OPTION_LABELS[i];
                    const isSelected = userAnswer === i;
                    const isCorrectOpt = question.answer === i;

                    let optStyle = "border-gray-200 text-gray-700 bg-white hover:bg-gray-50";
                    if (answered) {
                      if (isCorrectOpt) optStyle = "border-green-400 bg-green-50 text-green-700";
                      else if (isSelected) optStyle = "border-red-400 bg-red-50 text-red-600";
                    } else if (isSelected) {
                      optStyle = "border-indigo-400 bg-indigo-50 text-indigo-700";
                    }

                    return (
                      <button
                        key={i}
                        onClick={() => handleSelect(i)}
                        disabled={answered}
                        className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg border text-sm transition-colors ${optStyle} disabled:cursor-default`}
                      >
                        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                          answered && isCorrectOpt
                            ? "bg-green-500 text-white"
                            : answered && isSelected
                            ? "bg-red-400 text-white"
                            : "bg-gray-100 text-gray-500"
                        }`}>
                          {label}
                        </span>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-32">
                <p className="text-gray-400 text-sm">该题型暂无练习题</p>
              </div>
            )}
          </div>
        </div>

        {/* Feedback bar */}
        {answered && question && (
          <div className="border border-gray-200 rounded-xl px-4 py-3 mb-3 bg-white">
            {/* Row 1: 正确答案 + 知识点 */}
            <div className="flex items-center gap-4 flex-wrap">
              <span className="text-sm text-gray-600">
                正确答案 <strong className="text-green-600">{OPTION_LABELS[question.answer]}</strong>
                　您选择 <strong className={isCorrect ? "text-green-600" : "text-red-500"}>
                  {OPTION_LABELS[userAnswer]}
                </strong>
                {isCorrect ? (
                  <span className="ml-1 text-green-500">✓</span>
                ) : (
                  <span className="ml-1 text-red-400">✗</span>
                )}
              </span>
              <div className="flex items-center gap-1 text-xs text-gray-400 ml-auto">
                <BookOpen size={12} />
                <span>{question.knowledgePoint}</span>
              </div>
            </div>
            {/* Row 2: 作答统计 + 难度 */}
            <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
              <span>本人作答 1次，正确率 {isCorrect ? "100.00" : "0.00"}%</span>
              <span className="flex items-center gap-1">
                难度 <DifficultyStars difficulty={question.difficulty} />
              </span>
            </div>
            {/* Row 3: AI解析 */}
            {question.explanation && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <p className="text-xs font-semibold text-indigo-600 mb-1.5">AI解析</p>
                <p className="text-xs text-gray-600 leading-relaxed">{question.explanation}</p>
              </div>
            )}
          </div>
        )}

        {/* Type tabs — hidden when a fixed questionSet is provided */}
        {questionSet == null && (
          <div className="flex gap-3">
            {Object.entries(TYPE_LABELS).map(([type, label]) => (
              <button
                key={type}
                onClick={() => handleTypeChange(type)}
                className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-colors ${
                  activeType === type
                    ? "border-green-500 text-green-700 bg-green-50"
                    : "border-gray-200 text-gray-500 hover:bg-gray-50"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Right: answer sheet */}
      <AnswerSheetCard
        questions={list}
        answers={answers}
        currentIndex={currentIndex}
        onJump={handleJump}
        onReset={handleReset}
        stats={stats}
        isFlashcard={isFlashcard}
        onFlashcardToggle={() => setIsFlashcard((v) => !v)}
      />
    </div>
  );
}
